import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'

import { PublicLayout } from '../components/layout/PublicLayout'
import { ScrollToTop } from '../components/layout/ScrollToTop'
import { LocaleUrlSync } from '../i18n/LocaleUrlSync'
import { ReviewNoteWidget } from '../components/common/ReviewNoteWidget'
import { HomePage } from '../features/home/HomePage'
import { HousesPage } from '../features/houses/HousesPage'
import { HouseDetailPage } from '../features/houses/HouseDetailPage'
import { ExplorePage } from '../features/explore/ExplorePage'
import { DestinationPage } from '../features/explore/DestinationPage'
import { ActivitiesPage } from '../features/activities/ActivitiesPage'
import { HotelsPage } from '../features/hotels/HotelsPage'
import { ServicesPage } from '../features/services/ServicesPage'
import { BookingLookupPage } from '../features/booking/BookingLookupPage'
import { AboutPage } from '../features/home/AboutPage'
import { GalleryPage } from '../features/home/GalleryPage'
import { ContactPage } from '../features/home/ContactPage'
import { NotFoundPage } from '../components/common/NotFoundPage'

/**
 * The admin console is a lazy chunk.
 *
 * A guest on a phone at the lake should never download the calendar editor, the booking
 * table or the translation forms. Everything under /admin is code-split away from the
 * marketing bundle, which is also why the router keeps the two trees separate rather than
 * nesting admin routes inside the public layout.
 */
const AdminApp = lazy(() =>
  import('../features/admin/AdminApp').then((module) => ({ default: module.AdminApp })),
)

function RouteFallback() {
  return (
    <Box sx={{ position: 'fixed', insetInlineStart: 0, insetInlineEnd: 0, top: 0, zIndex: 2000 }}>
      <LinearProgress />
    </Box>
  )
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <LocaleUrlSync />
      <ReviewNoteWidget />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route index element={<HomePage />} />
            <Route path="houses" element={<HousesPage />} />
            <Route path="houses/:slug" element={<HouseDetailPage />} />
            <Route path="explore" element={<ExplorePage />} />
            <Route path="explore/:slug" element={<DestinationPage />} />
            <Route path="activities" element={<ActivitiesPage />} />
            <Route path="hotels" element={<HotelsPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="booking" element={<BookingLookupPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="gallery" element={<GalleryPage />} />
            <Route path="contact" element={<ContactPage />} />
            {/* The old listing linked to /cottages; keep the link alive. */}
            <Route path="cottages" element={<Navigate to="/houses" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </Suspense>
    </>
  )
}
